/**
 * PremiumSearchBar Component - iOS-Style Minimal Design
 * 
 * Features:
 * 1. Clean, borderless pill shape with search icon inside input
 * 2. Search icon on left (LTR) or right (RTL)
 * 3. Subtle animated green focus glow
 * 4. NO black background search button
 * 5. Camera icon for visual search
 * 6. Full RTL/LTR support
 * 
 * Privacy: "We don't store these images. Used only for search."
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PremiumSearchBar.css';
import { useTranslation } from 'react-i18next';
import {
  getRecentSearches,
  addRecentSearch,
  clearRecentSearches,
} from '../lib/recentSearches';

/**
 * NOTE: the internal `wassla_recent_searches_v1*` store used to live
 * here as a duplicate of `lib/recentSearches.js`. It has been removed
 * to eliminate a cross-user leak vector: this component now delegates
 * entirely to the shared, user-scoped v2 store. Anonymous users (no
 * `userId` prop) simply don't persist.
 */

export default function PremiumSearchBar({
  value,
  onChange,
  onSearch,
  onOpenFilters,
  onImageSearch,
  onImageResults,
  placeholder,
  dir = 'ltr',
  hasActiveFilters = false,
  showFilters = true,
  showCamera = true,
  userId,
}) {
  const { t } = useTranslation();
  const isRTL = dir === 'rtl';
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const [recentOpen, setRecentOpen] = useState(false);
  const [recent, setRecent] = useState(() => getRecentSearches(userId));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loadingImg, setLoadingImg] = useState(false);

  // Re-load recent searches when userId changes (account switch). The
  // lib util returns [] for invalid/guest IDs, so anonymous sessions
  // naturally show no persisted searches — no leakage across users.
  useEffect(() => {
    setRecent(getRecentSearches(userId));
  }, [userId]);

  const showClear = Boolean(value && value.length);
  const ph = placeholder || (t('home.searchProductsServicesJobs'));

  const recentFiltered = useMemo(() => {
    return recent.filter(Boolean);
  }, [recent]);

  // Close dropdown on outside click / ESC
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) {
        setRecentOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setRecentOpen(false);
        if (!loadingImg) setSheetOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [loadingImg]);

  const commitSearch = (q) => {
    const query = (q || '').trim();
    if (!query) return;

    // Persist only for real users (anonymous = no-op inside util).
    addRecentSearch(query, userId);
    setRecent(getRecentSearches(userId));

    setRecentOpen(false);
    onSearch?.(query);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') commitSearch(value);
  };

  // --- IMAGE SEARCH FLOW ---
  const fileInputGalleryRef = useRef(null);
  const fileInputCameraRef = useRef(null);

  const openImageSheet = () => setSheetOpen(true);

  const pickFromGallery = () => {
    fileInputGalleryRef.current?.click();
  };

  const takePhoto = async () => {
    // Trigger browser camera permission popup via getUserMedia
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      // Permission granted — stop stream immediately (we only needed the prompt)
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.warn('Camera permission denied or unavailable:', err.name);
      // Still attempt file input — some browsers handle it without getUserMedia
    }

    // Open camera capture input (mobile will open camera)
    fileInputCameraRef.current?.click();
  };

  /**
   * Preprocess image: resize to max 1024px, convert to JPEG
   * This ensures consistent input for both camera and gallery uploads
   */
  const preprocessImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        // Calculate resize dimensions (max 1024px on longest side)
        const MAX_SIZE = 1024;
        let { width, height } = img;
        
        if (width > MAX_SIZE || height > MAX_SIZE) {
          if (width > height) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          } else {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and convert to JPEG
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], 'query.jpg', { type: 'image/jpeg' }));
            } else {
              reject(new Error('Failed to convert image'));
            }
          },
          'image/jpeg',
          0.85  // Quality 85%
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const onPickedFile = async (file) => {
    if (!file) return;
    
    setLoadingImg(true);
    
    try {
      const processedFile = await preprocessImage(file);
      const formData = new FormData();
      formData.append('image', processedFile, 'query.jpg');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/search/image?limit=30`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error('Image search failed');
      
      const data = await response.json();
      setSheetOpen(false);
      
      if (onImageResults) {
        onImageResults(data.results || []);
      }
      if (onImageSearch) {
        onImageSearch(file);
      }
    } catch (e) {
      console.error('[PremiumSearchBar] Image search error:', e);
      const rtl = document.documentElement.lang === 'ar';
      alert(rtl ? 'فشل البحث بالصورة. حاول مرة أخرى.' : 'Image search failed. Please try again.');
    } finally {
      setLoadingImg(false);
    }
  };

  // Determine if filter button should be shown
  const shouldShowFilterBtn = showFilters && onOpenFilters;

  return (
    <div className={`psbWrap ${isRTL ? 'rtl' : 'ltr'}`} dir={dir} ref={wrapRef} data-testid="premium-search-bar">
      {/* Filter button - ONLY shown when showFilters=true AND onOpenFilters is provided */}
      {shouldShowFilterBtn && (
        <button 
          type="button" 
          className={`psbFilterBtn ${hasActiveFilters ? 'hasFilters' : ''}`}
          onClick={onOpenFilters} 
          aria-label={t('home.filters')}
          data-testid="psb-filter-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M10 18h4v-2h-4v2zM3 11h18V9H3v2zm3 7h12v-2H6v2zM7 6V4h10v2H7z" />
          </svg>
          {hasActiveFilters && <span className="psbFilterDot" />}
        </button>
      )}

      <div className="psbPill">
        {/* Search icon INSIDE pill - positioned based on RTL/LTR */}
        <div className="psbSearchIcon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M10 2a8 8 0 1 0 4.9 14.3l4.4 4.4 1.4-1.4-4.4-4.4A8 8 0 0 0 10 2zm0 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12z"
            />
          </svg>
        </div>

        <input
          ref={inputRef}
          className="psbInput"
          value={value}
          onChange={(e) => {
            onChange?.(e.target.value);
          }}
          placeholder={ph}
          onFocus={() => setRecentOpen(true)}
          onKeyDown={onKeyDown}
          inputMode="search"
          data-testid="psb-input"
        />

        {/* Clear button */}
        {showClear && (
          <button 
            type="button" 
            className="psbClearBtn" 
            onClick={() => onChange?.('')} 
            aria-label={t('psb.clear')}
            data-testid="psb-clear-btn"
          >
            ✕
          </button>
        )}

        {/* Camera icon for visual search */}
        {showCamera && (
        <button 
          type="button" 
          className="psbCamBtn" 
          onClick={openImageSheet} 
          aria-label={t('psb.searchWithImage')}
          data-testid="psb-camera-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M9 3 7.5 5H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2.5L15 3H9zm3 16a6 6 0 1 1 0-12 6 6 0 0 1 0 12zm0-2.2a3.8 3.8 0 1 0 0-7.6 3.8 3.8 0 0 0 0 7.6z"
            />
          </svg>
        </button>
        )}

        {/* Recent searches dropdown */}
        {recentOpen && (
          <div className="psbRecent" data-testid="psb-recent-dropdown">
            <div className="psbRecentHead">
              <span className="psbRecentTitle">
                {t('home.recentSearches')}
              </span>
              <button
                type="button"
                className="psbRecentClose"
                onClick={() => setRecentOpen(false)}
                aria-label={t('rolesele.close')}
                data-testid="psb-recent-close"
              >
                ✕
              </button>
            </div>

            {recentFiltered.length === 0 ? (
              <div className="psbRecentEmpty">
                {t('psb.noRecentSearchesYet')}
              </div>
            ) : (
              <ul className="psbRecentList">
                {recentFiltered.map((q, idx) => (
                  <li key={`${q}-${idx}`}>
                    <button
                      type="button"
                      className="psbRecentItem"
                      onClick={() => {
                        onChange?.(q);
                        commitSearch(q);
                      }}
                      data-testid={`psb-recent-item-${idx}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" className="psbRecentIcon">
                        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                        <path stroke="currentColor" strokeWidth="2" d="M12 6v6l4 2"/>
                      </svg>
                      <span>{q}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {recentFiltered.length > 0 && (
              <button
                type="button"
                className="psbRecentClearAll"
                onClick={() => {
                  clearRecentSearches(userId);
                  setRecent([]);
                }}
                data-testid="psb-clear-all"
              >
                {t('home.clearAll')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Hidden file inputs for image picking */}
      <input
        ref={fileInputGalleryRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // Reset for re-selection
          if (f) onPickedFile(f);
        }}
        data-testid="psb-gallery-input"
      />

      <input
        ref={fileInputCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = ''; // Reset for re-selection
          if (f) onPickedFile(f);
        }}
        data-testid="psb-camera-input"
      />

      {/* Bottom sheet: Search with image */}
      {sheetOpen && (
        <div 
          className="psbSheetOverlay" 
          role="dialog" 
          aria-modal="true" 
          onClick={() => !loadingImg && setSheetOpen(false)}
          data-testid="psb-image-sheet"
        >
          <div className="psbSheet" onClick={(e) => e.stopPropagation()}>
            <div className="psbSheetHandle" />
            
            <div className="psbSheetTop">
              <div className="psbSheetTitle">
                {t('psb.searchWithImage')}
              </div>
              <div className="psbSheetSub">
                {t('psb.wellShowVisuallySimilarAds')}
              </div>
            </div>

            <button 
              type="button" 
              className="psbSheetPrimary" 
              onClick={takePhoto}
              disabled={loadingImg}
              data-testid="psb-take-photo"
            >
              {loadingImg ? (
                <span className="psbSheetIcon">⏳</span>
              ) : (
                <span className="psbSheetIcon">📷</span>
              )}
              {loadingImg 
                ? (t('psb.searching')) 
                : (t('psb.takePhoto'))}
            </button>

            <button 
              type="button" 
              className="psbSheetSecondary" 
              onClick={pickFromGallery}
              disabled={loadingImg}
              data-testid="psb-select-photo"
            >
              {loadingImg ? (
                <span className="psbSheetIcon">⏳</span>
              ) : (
                <span className="psbSheetIcon">🖼️</span>
              )}
              {loadingImg 
                ? (t('psb.searching')) 
                : (t('psb.selectAnExistingPhoto'))}
            </button>

            <button 
              type="button" 
              className="psbSheetCancel" 
              onClick={() => !loadingImg && setSheetOpen(false)}
              disabled={loadingImg}
              data-testid="psb-cancel-sheet"
            >
              {t('bam.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
